import { useState, useEffect } from 'react';
import ObfuscatedContact from '@/components/base/ObfuscatedContact';

const CONTACT_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'ContactPage',
      '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/contact#webpage`,
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/contact`,
      name: 'Contact MAX-Sourcing | Pharmaceutical Sourcing & Fine Chemicals Enquiries',
      description:
        'Contact MAX-Sourcing for pharmaceutical sourcing, fine chemicals APIs, and cGMP supply chain enquiries. We serve clients across North America, Europe, China, and India.',
      inLanguage: 'en',
      isPartOf: {
        '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/#website`,
      },
      dateModified: '2026-04-07',
      translationOfWork: {
        '@type': 'ContactPage',
        '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/fr/contact#webpage`,
        url: `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/fr/contact`,
        inLanguage: 'fr',
      },
      workTranslation: {
        '@type': 'ContactPage',
        '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/fr/contact#webpage`,
        url: `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/fr/contact`,
        inLanguage: 'fr',
      },
    },
    {
      '@type': 'LocalBusiness',
      '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/#localbusiness`,
      name: 'MAX-Sourcing',
      url: import.meta.env.VITE_SITE_URL ?? 'https://example.com',
      telephone: '+1-819-923-0918',
      email: 'contact@MAX-Sourcing.com',
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'CA',
      },
      areaServed: [
        { '@type': 'Country', name: 'Canada' },
        { '@type': 'Country', name: 'United States' },
        { '@type': 'Country', name: 'United Kingdom' },
        { '@type': 'Country', name: 'China' },
        { '@type': 'Country', name: 'India' },
      ],
      description:
        'MAX-Sourcing is a global pharmaceutical sourcing and fine chemicals company providing cGMP-compliant supply chain solutions.',
    },
  ],
};

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: '',
    website_url: '', // honeypot — must stay empty
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Contact MAX-Sourcing | Pharmaceutical Sourcing & Fine Chemicals Enquiries';
    const id = 'schema-contact';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(CONTACT_SCHEMA);
    return () => {
      document.getElementById(id)?.remove();
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Honeypot check — bots fill this, humans don't
    if (formData.website_url) return;
    if (formData.message.length > 500) {
      setError('Message must be 500 characters or fewer.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => {
        if (k !== 'website_url') body.append(k, v); // exclude honeypot from payload
      });
      const res = await fetch('https://readdy.ai/api/form/d7a1rfbll6jbq2vde010', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Hero */}
      <section className="w-full bg-stone-800 pt-36 pb-20 px-6 md:px-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 70% 50%, #10b981 0%, transparent 60%)' }}
        />
        <div className="max-w-7xl mx-auto relative">
          <span className="text-emerald-400 text-xs font-bold uppercase tracking-widest">Get In Touch</span>
          <h1
            className="text-4xl md:text-5xl font-extrabold text-white mt-3 mb-4"
            style={{ fontFamily: "'DM Serif Display', serif" }}
          >
            Contact Us
          </h1>
          <p className="text-white/60 text-base max-w-xl leading-relaxed">
            Have a sourcing challenge or want to learn more about our services? Reach out — we respond promptly.
          </p>
        </div>
      </section>

      {/* Main content */}
      <section className="w-full py-20 px-6 md:px-16 flex-1">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-12">

          {/* Contact info */}
          <div className="flex flex-col gap-8">
            <div>
              <h2
                className="text-2xl font-extrabold text-stone-800 mb-2"
                style={{ fontFamily: "'DM Serif Display', serif" }}
              >
                Reach Us Directly
              </h2>
              <div className="w-10 h-0.5 bg-emerald-600 mb-6" />
            </div>

            <div className="flex flex-col gap-6">
              {/* Phone */}
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-emerald-50 rounded-lg text-emerald-600 shrink-0">
                  <i className="ri-phone-line text-xl" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">Telephone</p>
                  <ObfuscatedContact
                    type="phone"
                    className="text-stone-800 font-semibold text-base hover:text-emerald-700 transition-colors cursor-pointer"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-emerald-50 rounded-lg text-emerald-600 shrink-0">
                  <i className="ri-mail-line text-xl" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">Email</p>
                  <ObfuscatedContact
                    type="email"
                    className="text-stone-800 font-semibold text-base hover:text-emerald-700 transition-colors cursor-pointer break-all"
                  />
                </div>
              </div>

              {/* Location */}
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-emerald-50 rounded-lg text-emerald-600 shrink-0">
                  <i className="ri-map-pin-line text-xl" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">Regions Served</p>
                  <p className="text-stone-700 text-sm leading-relaxed">North America · Europe · China · India</p>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-stone-100 pt-6">
              <p className="text-xs text-gray-400 leading-relaxed">
                All inquiries are handled with complete confidentiality. We typically respond within one business day.
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-2">
            <div className="bg-stone-50 rounded-xl border border-stone-100 p-8 md:p-10">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
                  <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full text-emerald-600">
                    <i className="ri-check-line text-3xl" />
                  </div>
                  <h3
                    className="text-2xl font-extrabold text-stone-800"
                    style={{ fontFamily: "'DM Serif Display', serif" }}
                  >
                    Message Sent!
                  </h3>
                  <p className="text-gray-500 text-sm max-w-sm">
                    Thank you for reaching out. We&apos;ll get back to you within one business day.
                  </p>
                </div>
              ) : (
                <>
                  <h3
                    className="text-xl font-extrabold text-stone-800 mb-1"
                    style={{ fontFamily: "'DM Serif Display', serif" }}
                  >
                    Send Us a Message
                  </h3>
                  <p className="text-sm text-gray-400 mb-8">Fill in the form below and we&apos;ll be in touch shortly.</p>

                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-5"
                  >
                    {/* Honeypot — hidden from real users, bots fill it in */}
                    <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }} aria-hidden="true">
                      <input
                        type="text"
                        name="website_url"
                        value={formData.website_url}
                        onChange={handleChange}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold uppercase tracking-widest text-gray-500">
                          Full Name <span className="text-emerald-600">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Jane Smith"
                          className="border border-stone-200 rounded-md px-4 py-2.5 text-sm text-stone-800 bg-white focus:outline-none focus:border-emerald-500 transition-colors"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold uppercase tracking-widest text-gray-500">
                          Email <span className="text-emerald-600">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="jane@company.com"
                          className="border border-stone-200 rounded-md px-4 py-2.5 text-sm text-stone-800 bg-white focus:outline-none focus:border-emerald-500 transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold uppercase tracking-widest text-gray-500">Company</label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Your Company Inc."
                          className="border border-stone-200 rounded-md px-4 py-2.5 text-sm text-stone-800 bg-white focus:outline-none focus:border-emerald-500 transition-colors"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold uppercase tracking-widest text-gray-500">Phone</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+1 000-000-0000"
                          className="border border-stone-200 rounded-md px-4 py-2.5 text-sm text-stone-800 bg-white focus:outline-none focus:border-emerald-500 transition-colors"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-semibold uppercase tracking-widest text-gray-500">Subject</label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="border border-stone-200 rounded-md px-4 py-2.5 text-sm text-stone-800 bg-white focus:outline-none focus:border-emerald-500 transition-colors cursor-pointer"
                      >
                        <option value="">Select a topic...</option>
                        <option value="Global Sourcing Inquiry">Global Sourcing Inquiry</option>
                        <option value="De-Risking & Custom Development">De-Risking &amp; Custom Development</option>
                        <option value="Supplier Partnership">Supplier Partnership</option>
                        <option value="Quality & Compliance">Quality &amp; Compliance</option>
                        <option value="General Inquiry">General Inquiry</option>
                      </select>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-semibold uppercase tracking-widest text-gray-500">
                        Message <span className="text-emerald-600">*</span>
                      </label>
                      <textarea
                        name="message"
                        required
                        rows={5}
                        maxLength={500}
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your sourcing needs or inquiry..."
                        className="border border-stone-200 rounded-md px-4 py-2.5 text-sm text-stone-800 bg-white focus:outline-none focus:border-emerald-500 transition-colors resize-none"
                      />
                      <p className="text-xs text-gray-400 text-right">{formData.message.length}/500</p>
                    </div>

                    {error && (
                      <p className="text-sm text-red-500">{error}</p>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold text-sm rounded-md py-3 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                    >
                      {submitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
